import React from 'react';
import { Link } from 'react-router-dom';

export default function Terms() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800">
      <div className="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-100 mb-2">
            Terms of Service
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            The rules and agreements that govern your use of EmotionWise
          </p>
        </div>

        {/* Content */}
        <div className="bg-slate-900/85 backdrop-blur-sm shadow-xl rounded-2xl border border-slate-700 overflow-hidden">
          <div className="p-6 sm:p-8 space-y-8">

            {/* 1. Acceptance */}
            <section>
              <h2 className="text-2xl font-bold text-slate-100 mb-4">1. Acceptance of Terms</h2>
              <p className="text-slate-300 leading-relaxed">
                By creating an account or using the EmotionWise API or web application (collectively, the "Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree, do not use the Service.
              </p>
              <p className="text-slate-300 leading-relaxed mt-3">
                These Terms constitute a binding legal agreement between you and EmotionWise ("we", "us", "our"). If you are using the Service on behalf of a company or organization, you represent that you have the authority to bind that entity to these Terms.
              </p>
            </section>

            {/* 2. Eligibility */}
            <section>
              <h2 className="text-2xl font-bold text-slate-100 mb-4">2. Eligibility</h2>
              <p className="text-slate-300 leading-relaxed">
                You must be at least <strong className="text-slate-100">18 years of age</strong> to use the Service. By using the Service, you represent and warrant that you meet this age requirement.
              </p>
              <p className="text-slate-300 leading-relaxed mt-3">
                The Service is not directed at or intended for use by minors. We do not knowingly allow persons under 18 to create accounts. If we discover that an account was created by someone under 18, we will terminate that account immediately.
              </p>
            </section>

            {/* 3. Account Registration */}
            <section>
              <h2 className="text-2xl font-bold text-slate-100 mb-4">3. Account Registration</h2>
              <div className="space-y-3 text-slate-300 leading-relaxed">
                <p>
                  You must register an account to access most features of the Service. You agree to provide accurate, current, and complete information during registration and to keep your account information updated.
                </p>
                <p>
                  You are responsible for maintaining the confidentiality of your account credentials, including your password and API keys. You are responsible for all activity that occurs under your account.
                </p>
                <p>
                  <strong className="text-slate-100">One account per person.</strong> You may not create multiple accounts to circumvent usage limits, quotas, or any restriction imposed by these Terms or the Service. We reserve the right to terminate all accounts associated with a person or organization found to be doing so.
                </p>
                <p>
                  You must notify us immediately at{' '}
                  <a href="mailto:support@emotionwise.ai" className="text-indigo-300 hover:text-indigo-200 hover:underline transition-colors duration-200">
                    support@emotionwise.ai
                  </a>{' '}
                  if you suspect unauthorized access to your account.
                </p>
              </div>
            </section>

            {/* 4. Permitted Use */}
            <section>
              <h2 className="text-2xl font-bold text-slate-100 mb-4">4. Permitted Use</h2>
              <p className="text-slate-300 leading-relaxed mb-3">
                Subject to these Terms and your payment of applicable fees, we grant you a limited, non-exclusive, non-transferable right to access and use the Service for lawful purposes.
              </p>
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
                <h3 className="text-lg font-semibold text-emerald-200 mb-2">You may:</h3>
                <ul className="space-y-1 text-emerald-200/90 text-sm">
                  <li>Use the API to build applications and products for your customers.</li>
                  <li>Integrate emotion and sarcasm detection results into your own services.</li>
                  <li>Sublicense API output (results) to your end users as part of your product.</li>
                  <li>Use the Service for commercial purposes under a paid plan.</li>
                </ul>
              </div>
            </section>

            {/* 5. Prohibited Use */}
            <section>
              <h2 className="text-2xl font-bold text-slate-100 mb-4">5. Prohibited Use</h2>
              <p className="text-slate-300 leading-relaxed mb-3">
                You agree not to use the Service for any of the following:
              </p>
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                <ul className="space-y-2 text-red-200/90 text-sm">
                  <li><strong className="text-red-200">Child sexual abuse material (CSAM)</strong> — processing, generating, classifying, or facilitating any content that sexually exploits minors.</li>
                  <li><strong className="text-red-200">Illegal surveillance</strong> — monitoring, tracking, or profiling individuals without their lawful consent, including covert workplace surveillance or stalkerware applications.</li>
                  <li><strong className="text-red-200">Processing of minors' data</strong> — using the Service to analyze text generated by or about individuals under 18 years of age.</li>
                  <li><strong className="text-red-200">Illegal activity</strong> — any use that violates applicable local, state, national, or international law or regulation.</li>
                  <li><strong className="text-red-200">Abuse of service</strong> — attempting to reverse-engineer, scrape, or systematically extract training data; using automated tools to circumvent rate limits; or any activity that disproportionately burdens our infrastructure.</li>
                  <li><strong className="text-red-200">Reselling raw API access</strong> — selling direct access to the EmotionWise API endpoint itself as a pass-through without building a product on top of it.</li>
                  <li><strong className="text-red-200">Misrepresentation</strong> — representing that the Service provides guaranteed, clinically validated, or legally admissible emotion analysis results.</li>
                </ul>
              </div>
              <p className="text-slate-400 text-sm mt-3">
                Violation of these prohibitions may result in immediate account termination without notice and without refund.
              </p>
            </section>

            {/* 6. API Access and Quotas */}
            <section>
              <h2 className="text-2xl font-bold text-slate-100 mb-4">6. API Access and Quotas</h2>
              <div className="space-y-3 text-slate-300 leading-relaxed">
                <p>
                  Each plan includes a monthly quota of API calls as described on the{' '}
                  <Link to="/subscription_plans" className="text-indigo-300 hover:text-indigo-200 hover:underline transition-colors duration-200">
                    Pricing page
                  </Link>.
                  Quotas reset at the start of each billing cycle.
                </p>
                <p>
                  When your monthly quota is exhausted, API requests will be <strong className="text-slate-100">blocked</strong> until your quota resets. There is no automatic overage billing. To resume service before the reset, you must upgrade to a higher plan.
                </p>
                <p>
                  We reserve the right to apply rate limits to protect service availability for all users. Exceeding rate limits may result in temporary request throttling.
                </p>
                <p>
                  API keys are tied to your account. You are responsible for all usage under your API keys. Do not share your API keys publicly or in client-side code.
                </p>
              </div>
            </section>

            {/* 7. Billing and Payment */}
            <section>
              <h2 className="text-2xl font-bold text-slate-100 mb-4">7. Billing and Payment</h2>
              <div className="bg-slate-800/80 border border-slate-700 rounded-xl p-6 space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-200 mb-1">Subscriptions</h3>
                  <p className="text-slate-300 text-sm">
                    Paid plans are billed on a monthly recurring basis. By subscribing, you authorize us to charge your payment method each billing cycle until you cancel.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-200 mb-1">No Refunds</h3>
                  <p className="text-slate-300 text-sm">
                    All payments are <strong className="text-slate-100">final and non-refundable</strong>. We do not offer refunds or credits for partial months, unused quota, or plan downgrades. If you are unsatisfied with the Service, you may cancel at any time and your access will continue until the end of the current billing period.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-200 mb-1">Failed Payments</h3>
                  <p className="text-slate-300 text-sm">
                    If a payment fails, your account will be flagged as past due. Access to paid features may be restricted until payment is resolved. We may attempt to retry the charge in accordance with your payment processor's retry schedule.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-200 mb-1">Taxes</h3>
                  <p className="text-slate-300 text-sm">
                    Prices listed do not include taxes. You are responsible for any applicable taxes, levies, or duties imposed by your jurisdiction.
                  </p>
                </div>
              </div>
            </section>

            {/* 8. Cancellation */}
            <section>
              <h2 className="text-2xl font-bold text-slate-100 mb-4">8. Cancellation</h2>
              <div className="space-y-3 text-slate-300 leading-relaxed">
                <p>
                  You may cancel your subscription at any time through the billing portal accessible from your account settings. Cancellation takes effect at the end of the current billing period — you will retain access to your paid plan features until then.
                </p>
                <p>
                  Cancelling a subscription does not delete your account. Your account and API keys remain accessible on the free tier after cancellation. We do not store any analysis history.
                </p>
                <p>
                  To delete your account entirely, contact us at{' '}
                  <a href="mailto:support@emotionwise.ai" className="text-indigo-300 hover:text-indigo-200 hover:underline transition-colors duration-200">
                    support@emotionwise.ai
                  </a>.
                </p>
              </div>
            </section>

            {/* 9. Service Availability */}
            <section>
              <h2 className="text-2xl font-bold text-slate-100 mb-4">9. Service Availability</h2>
              <p className="text-slate-300 leading-relaxed">
                We provide the Service on a <strong className="text-slate-100">best-effort basis</strong>. We do not guarantee any specific uptime, availability, or response time. The Service may be temporarily unavailable due to scheduled maintenance, infrastructure issues, or circumstances beyond our control.
              </p>
              <p className="text-slate-300 leading-relaxed mt-3">
                We are not liable for any damages or losses resulting from Service downtime or degraded performance. We recommend against using the Service as the sole or critical dependency for safety-sensitive systems.
              </p>
            </section>

            {/* 10. Accuracy Disclaimer */}
            <section>
              <h2 className="text-2xl font-bold text-slate-100 mb-4">10. Accuracy Disclaimer</h2>
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                <p className="text-amber-200/90 text-sm leading-relaxed">
                  Emotion and sarcasm detection results are produced by machine learning models and are <strong className="text-amber-200">probabilistic, not definitive</strong>. Results may be inaccurate, incomplete, or culturally biased. They are not intended for use in clinical, legal, law enforcement, hiring, or other high-stakes decision-making contexts without independent human review. EmotionWise makes no warranty as to the accuracy, reliability, or fitness of detection results for any particular purpose.
                </p>
              </div>
            </section>

            {/* 11. Intellectual Property */}
            <section>
              <h2 className="text-2xl font-bold text-slate-100 mb-4">11. Intellectual Property</h2>
              <div className="space-y-3 text-slate-300 leading-relaxed">
                <p>
                  The Service, including its software, models, documentation, and branding, is owned by EmotionWise and protected by applicable intellectual property laws. You may not copy, modify, distribute, or reverse-engineer any part of the Service.
                </p>
                <p>
                  You retain ownership of the text you submit to the Service. We do not store any text you submit or any analysis results. Your text is processed in real time and discarded immediately after the response is returned.
                </p>
                <p>
                  API output (emotion labels, scores, and metadata) returned by the Service may be incorporated into your products and services.
                </p>
              </div>
            </section>

            {/* 12. Termination */}
            <section>
              <h2 className="text-2xl font-bold text-slate-100 mb-4">12. Termination</h2>
              <div className="space-y-3 text-slate-300 leading-relaxed">
                <p>
                  We reserve the right to suspend or terminate your account at any time, with or without notice, if we determine in our sole discretion that you have violated these Terms.
                </p>
                <p>
                  Upon termination for violation of these Terms, no refund will be issued for any remaining prepaid period.
                </p>
                <p>
                  We may also terminate or suspend the Service entirely with <strong className="text-slate-100">30 days' advance notice</strong> posted to our website or sent to your registered email address.
                </p>
              </div>
            </section>

            {/* 13. Limitation of Liability */}
            <section>
              <h2 className="text-2xl font-bold text-slate-100 mb-4">13. Disclaimer of Warranties &amp; Limitation of Liability</h2>
              <div className="bg-slate-800/80 border border-slate-700 rounded-xl p-6 space-y-3 text-slate-300 text-sm leading-relaxed">
                <p>
                  THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
                </p>
                <p>
                  TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, EMOTIONWISE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, DATA, OR BUSINESS, ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICE.
                </p>
                <p>
                  IN NO EVENT SHALL OUR TOTAL LIABILITY TO YOU EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID TO US IN THE THREE MONTHS IMMEDIATELY PRECEDING THE CLAIM, OR (B) FIFTY US DOLLARS ($50).
                </p>
              </div>
            </section>

            {/* 14. Changes to Terms */}
            <section>
              <h2 className="text-2xl font-bold text-slate-100 mb-4">14. Changes to These Terms</h2>
              <p className="text-slate-300 leading-relaxed">
                We may update these Terms from time to time. For non-material changes, we will update the "Last Updated" date at the bottom of this page. For <strong className="text-slate-100">material changes — including pricing changes</strong> — we will notify you by email to your registered address and/or by posting a notice in the Service at least <strong className="text-slate-100">30 days before</strong> the change takes effect.
              </p>
              <p className="text-slate-300 leading-relaxed mt-3">
                Your continued use of the Service after a change takes effect constitutes your acceptance of the updated Terms. If you do not agree to the updated Terms, you must cancel your account before the effective date.
              </p>
            </section>

            {/* 15. Governing Law */}
            <section>
              <h2 className="text-2xl font-bold text-slate-100 mb-4">15. Governing Law</h2>
              <p className="text-slate-300 leading-relaxed">
                These Terms are governed by and construed in accordance with the laws of the <strong className="text-slate-100">State of California, United States</strong>, without regard to its conflict of law provisions. Any disputes arising from these Terms or your use of the Service shall be resolved exclusively in the state or federal courts located in California.
              </p>
            </section>

            {/* 16. Contact */}
            <section>
              <h2 className="text-2xl font-bold text-slate-100 mb-4">16. Contact</h2>
              <div className="bg-slate-800/80 border border-slate-600 rounded-xl p-6">
                <p className="text-slate-300 mb-4">
                  If you have any questions about these Terms, please contact us:
                </p>
                <div className="space-y-2 text-slate-300">
                  <div>
                    <strong className="text-slate-100">General &amp; Billing:</strong>{' '}
                    <a
                      href="mailto:support@emotionwise.ai"
                      className="text-indigo-300 hover:text-indigo-200 hover:underline transition-colors duration-200"
                    >
                      support@emotionwise.ai
                    </a>
                  </div>
                  <div>
                    <strong className="text-slate-100">Legal &amp; Privacy:</strong>{' '}
                    <a
                      href="mailto:support@emotionwise.ai"
                      className="text-indigo-300 hover:text-indigo-200 hover:underline transition-colors duration-200"
                    >
                      support@emotionwise.ai
                    </a>
                  </div>
                  <p className="text-sm text-slate-400 mt-2">
                    We aim to respond to all inquiries within 2 business days.
                  </p>
                </div>
              </div>
            </section>

            {/* Footer */}
            <div className="border-t border-slate-700 pt-6 text-center">
              <p className="text-sm text-slate-500 mb-2">
                Last Updated: March 6, 2026
              </p>
              <p className="text-xs text-slate-600 mb-4">
                These Terms of Service apply to all users of emotionwise.ai.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/privacy"
                  className="text-sm text-indigo-300 hover:text-indigo-200 hover:underline transition-colors duration-200"
                >
                  Privacy Policy
                </Link>
                <Link
                  to="/"
                  className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all duration-200"
                >
                  ← Back to Home
                </Link>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
